import { DefaultMap } from "@glideapps/ts-necessities";
import sample from "lodash/sample";
import type { PrimitiveValue } from "../common/components/computation-types";
import { areValuesEqual } from "../common/components/primitives";
import type { GroundValue, LoadedGroundValue, LoadingValue, Row, Table } from "../common/computation-model/data";
import {
    asBoolean,
    asMaybeDate,
    asMaybeNumber,
    asMaybeString,
    asPrimitive,
    follow,
    isLoadingValue,
    isNotEmpty,
    isPrimitive,
    isRow,
    isTable,
    tableForEach,
    tableMap,
} from "../common/computation-model/data";
import type { Path, RelativePath } from "../common/computation-model/paths";
import { getSymbolicRepresentationForPath } from "../common/computation-model/paths";
import type {
    Dirt,
    DirtyState,
    ProcessDirtProcessor,
    RootPathResolver,
    TableAggregateComputation,
    TableAggregateDataProvider,
} from "../common/computation-model/types";
import { areSetsOverlapping, assert, defined, panic } from "../support";
import { getRelationKeys, getValueAt } from "./handlers";

interface ExtractRowByPositionState {
    currentKey: PrimitiveValue;
    currentRowID: string | undefined;
}

export enum FirstOrLast {
    First,
    Last,
}

abstract class ExtractRowByPositionComputation<TState extends ExtractRowByPositionState>
    implements TableAggregateComputation<TState>
{
    public abstract readonly displayName: string;
    public abstract readonly symbolicRepresentation: string;

    constructor(
        private readonly _keyPath: RelativePath,
        // `_valuePath` can be used to produce a column in the result
        // row, vs the whole result row.  That's how we implement Single
        // Value.
        private readonly _valuePath: RelativePath | undefined,
        private readonly _position: FirstOrLast
    ) {}

    // If this other dirt potentially invalidates the existing extremum,
    // `proc.setDirty()` must be called, which will force a full
    // recomputation.  In the future we might make it possible to be more
    // selective.
    public abstract processOtherDirt(proc: ProcessDirtProcessor, d: Dirt): boolean;

    public abstract recompute(
        proc: TableAggregateDataProvider<TState>,
        aggregatedTableDirtyState: DirtyState,
        contextTableDirtyState: DirtyState | undefined
    ): GroundValue;

    public getContextPaths(): readonly Path[] {
        return [];
    }

    public getAggregatePaths(): readonly Path[] {
        if (this._valuePath === undefined) {
            return [this._keyPath];
        } else {
            return [this._keyPath, this._valuePath];
        }
    }

    protected getKeyForRow(r: Row): PrimitiveValue | LoadingValue {
        const v = follow(r, this._keyPath);
        if (isLoadingValue(v)) return v;
        return asPrimitive(v);
    }

    protected doesNewKeyOverride(newKey: PrimitiveValue, oldKey: PrimitiveValue): boolean {
        assert(newKey !== undefined && oldKey !== undefined);
        if (this._position === FirstOrLast.First) {
            return newKey < oldKey;
        } else {
            return newKey > oldKey;
        }
    }

    protected getResultForState(state: TState, table: Table): GroundValue {
        if (state.currentRowID === undefined) return undefined;
        const row = defined(table.get(state.currentRowID));
        if (this._valuePath === undefined) {
            return row;
        } else {
            return follow(row, this._valuePath);
        }
    }
}

// Produces either the first or last row, sorted by some value path, and
// given a filter condition for rows.  We use this to
// * Get the first and last rows in a table.  In this case the sort value
//   is the row index, and the filter allows all rows.
// * Compute a single relation.  That's the first row, by row index, where
//   the filter only allows rows matching the relation.
//
// The way this works is that we're keeping track of the current minimum
// key and row.  In most circumstances we won't have to do much recomputation.
abstract class ExtractMatchingRowByExtremumComputation extends ExtractRowByPositionComputation<ExtractRowByPositionState> {
    protected abstract isRowIncluded(r: Row, proc: TableAggregateDataProvider<ExtractRowByPositionState>): boolean;

    constructor(
        keyPath: RelativePath,
        // `_valuePath` can be used to produce a column in the result
        // row, vs the whole result row.  That's how we implement Single
        // Value.
        valuePath: RelativePath | undefined,
        position: FirstOrLast,
        protected readonly includeInvisible: boolean
    ) {
        super(keyPath, valuePath, position);
    }

    public recompute(
        proc: TableAggregateDataProvider<ExtractRowByPositionState>,
        aggregatedTableDirtyState: DirtyState,
        contextDirtyState: DirtyState | undefined
    ): GroundValue {
        let maybeState = proc.get();
        if (maybeState === undefined) {
            maybeState = { currentRowID: undefined, currentKey: undefined };
            proc.set(maybeState);
        }
        const state = maybeState;

        let searchWholeTable = false;
        const table = proc.getAggregatedTable();
        if (isLoadingValue(table)) return table;

        if (state.currentRowID !== undefined) {
            const row = table?.get(state.currentRowID);
            if (row === undefined || this.getKeyForRow(row) !== state.currentKey || !this.isRowIncluded(row, proc)) {
                state.currentKey = undefined;
                state.currentRowID = undefined;
                searchWholeTable = true;
            }
        }

        if (table === undefined) return undefined;

        const { dirtyRowIDs } = aggregatedTableDirtyState;
        assert(dirtyRowIDs !== false || contextDirtyState?.isDirty === true);

        let loadingValue: LoadingValue | undefined;
        const processRow = (row: Row) => {
            if (!row.$isVisible) return;
            if (!this.isRowIncluded(row, proc)) return;

            const key = this.getKeyForRow(row);
            if (isLoadingValue(key)) {
                loadingValue = key;
                return;
            }

            if (
                state.currentRowID === undefined ||
                (key !== undefined &&
                    (state.currentKey === undefined || this.doesNewKeyOverride(key, state.currentKey)))
            ) {
                state.currentRowID = row.$rowID;
                state.currentKey = key;
            }
        };

        // Are we too conservative here wth `searchWholeTable`?  Should we
        // always search the whole table if `state.currentRowID` is
        // `undefined`?  Technically that shouldn't be necessary, since it was
        // also `undefined` last time we recomputed, only dirty rows can
        // possibly make a difference.
        if (searchWholeTable || dirtyRowIDs === true || contextDirtyState?.isDirty === true) {
            tableForEach(table, this.includeInvisible, processRow);
        } else {
            assert(typeof dirtyRowIDs !== "boolean");
            for (const rowID of dirtyRowIDs) {
                const row = table.get(rowID);
                if (row === undefined) continue;
                processRow(row);
            }
        }

        if (loadingValue !== undefined) return loadingValue;

        return this.getResultForState(state, table);
    }
}

export class ExtractRowByMinimumComputation extends ExtractMatchingRowByExtremumComputation {
    public readonly displayName = "first";
    public readonly symbolicRepresentation: string;

    constructor(keyPath: RelativePath, valuePath: RelativePath | undefined, includeInvisible: boolean) {
        super(keyPath, valuePath, FirstOrLast.First, includeInvisible);

        this.symbolicRepresentation = `(extract-row by-minimum: ${getSymbolicRepresentationForPath(
            keyPath
        )} value: ${getSymbolicRepresentationForPath(valuePath)})`;
    }

    protected isRowIncluded(r: Row): boolean {
        return this.includeInvisible || r.$isVisible;
    }

    public processOtherDirt(): boolean {
        return panic("This doesn't use other paths");
    }
}

export class ExtractRowByMaximumComputation extends ExtractMatchingRowByExtremumComputation {
    public readonly displayName = "last";
    public readonly symbolicRepresentation: string;

    constructor(keyPath: RelativePath, valuePath: RelativePath | undefined) {
        super(keyPath, valuePath, FirstOrLast.Last, false);

        this.symbolicRepresentation = `(extract-row by-maximum: ${getSymbolicRepresentationForPath(
            keyPath
        )} value: ${getSymbolicRepresentationForPath(valuePath)})`;
    }

    protected isRowIncluded(r: Row): boolean {
        assert(r.$isVisible);
        return true;
    }

    public processOtherDirt(): boolean {
        return panic("This doesn't use other paths");
    }
}

export class SingleRelationComputation extends ExtractMatchingRowByExtremumComputation {
    public readonly displayName = "singleValue";
    public readonly symbolicRepresentation: string;

    constructor(
        private readonly _sourceKeyColumnPath: Path, // in the context, or global
        private readonly _targetKeyColumnPath: Path, // in the matching rows, or global
        sortKeyPath: RelativePath,
        private readonly _sourceKeyIsArray: boolean,
        private readonly _targetKeyIsArray: boolean
    ) {
        super(sortKeyPath, undefined, FirstOrLast.First, false);

        this.symbolicRepresentation = `(single-relation source-column: ${getSymbolicRepresentationForPath(
            _sourceKeyColumnPath
        )} target-column: ${getSymbolicRepresentationForPath(
            _targetKeyColumnPath
        )} sort-by: ${getSymbolicRepresentationForPath(sortKeyPath)})`;
    }

    public getContextPaths(): readonly Path[] {
        return [...super.getContextPaths(), this._sourceKeyColumnPath];
    }

    public getAggregatePaths(): readonly Path[] {
        return [...super.getAggregatePaths(), this._targetKeyColumnPath];
    }

    protected isRowIncluded(r: Row, proc: TableAggregateDataProvider<ExtractRowByPositionState>): boolean {
        assert(r.$isVisible);

        // FIXME: Get the key only once for all rows, in
        // `ExtractMatchingRowByExtremumComputation.recompute`.
        const keys = new Set(
            getRelationKeys(
                getValueAt(proc.rootPathResolver, proc.getContextRow(), this._sourceKeyColumnPath),
                this._sourceKeyIsArray
            )
        );
        const values = getRelationKeys(
            getValueAt(proc.rootPathResolver, r, this._targetKeyColumnPath),
            this._targetKeyIsArray
        );
        return areSetsOverlapping(keys, values);
    }

    public processOtherDirt(proc: ProcessDirtProcessor): boolean {
        return proc.setDirty();
    }
}

interface ExtractRandomRowState {
    rowID: string | undefined;
    lastNumRows: number;
}

let mockRandomPickIndex: number | undefined;
export function mockNewComputationModelRandomPick(index: number | undefined): void {
    mockRandomPickIndex = index;
}

export class ExtractRandomRowComputation implements TableAggregateComputation<ExtractRandomRowState, readonly Row[]> {
    public readonly displayName = "random";

    constructor(
        // `_valuePath` can be used to produce a column in the result
        // row, vs the whole result row.  That's how we implement Single
        // Value.
        private readonly _valuePath: RelativePath | undefined
    ) {}

    public getContextPaths(): readonly Path[] {
        return [];
    }

    public getAggregatePaths(): readonly Path[] {
        if (this._valuePath === undefined) {
            return [];
        } else {
            return [this._valuePath];
        }
    }

    public processOtherDirt(): boolean {
        return panic("This doesn't use other paths");
    }

    public recompute(proc: TableAggregateDataProvider<ExtractRandomRowState, readonly Row[]>): GroundValue {
        let maybeState = proc.get();
        if (maybeState === undefined) {
            maybeState = { rowID: undefined, lastNumRows: 0 };
            proc.set(maybeState);
        }
        const state = maybeState;

        let table = proc.getAggregatedTable();
        if (isLoadingValue(table)) return table;
        if (table === undefined) {
            table = new Map();
        }

        function getTableRows() {
            return defined(proc.deriveFromAggregatedTable(t => tableMap(t, false, r => r)));
        }

        if (mockRandomPickIndex !== undefined) {
            const rows = getTableRows();
            if (isLoadingValue(rows)) return rows;
            if (rows.length === 0) {
                state.rowID = undefined;
            } else {
                state.rowID = defined(rows[mockRandomPickIndex % rows.length]).$rowID;
            }
        } else {
            if (state.rowID !== undefined) {
                const row = table.get(state.rowID);
                if (row === undefined) {
                    state.rowID = undefined;
                }
            }

            if (state.rowID === undefined || state.lastNumRows !== table.size) {
                const rows = getTableRows();
                if (isLoadingValue(rows)) return rows;
                // This will be `undefined` if `table` is empty.
                state.rowID = sample(rows)?.$rowID;
                state.lastNumRows = table.size;
            }
        }

        if (state.rowID === undefined) return undefined;
        const row = defined(table.get(state.rowID));
        if (this._valuePath === undefined) {
            return row;
        } else {
            return follow(row, this._valuePath);
        }
    }

    public get symbolicRepresentation(): string {
        return `(extract-random-row value: ${getSymbolicRepresentationForPath(this._valuePath)})`;
    }
}

export class ExtractRandomItemComputation implements TableAggregateComputation<GroundValue> {
    public readonly displayName = "randomItem";
    public readonly symbolicRepresentation = "(extract-random-item)";

    public getContextPaths(): readonly Path[] {
        return [];
    }

    public getAggregatePaths(): readonly Path[] {
        return [];
    }

    public processOtherDirt(): boolean {
        return panic("This doesn't use other paths");
    }

    public recompute(proc: TableAggregateDataProvider<GroundValue>): GroundValue {
        let maybeItem = proc.get();

        const array = proc.getAggregatedAsArray();
        if (array === undefined || isLoadingValue(array)) {
            proc.set(array);
            return array;
        }

        if (array.includes(maybeItem)) {
            return maybeItem;
        }

        maybeItem = sample(array);
        proc.set(maybeItem);
        return maybeItem;
    }
}
export class ExtractNthRowComputation extends ExtractRowByPositionComputation<ExtractRowByPositionState> {
    public readonly displayName = "nth";
    public readonly symbolicRepresentation: string;

    constructor(
        keyPath: RelativePath,
        // `_valuePath` can be used to produce a column in the result
        // row, vs the whole result row.  That's how we implement Single
        // Value.
        valuePath: RelativePath | undefined,
        position: FirstOrLast,
        private readonly _offsetPath: Path
    ) {
        super(keyPath, valuePath, position);

        this.symbolicRepresentation = `(extract-nth-row ${getSymbolicRepresentationForPath(
            _offsetPath
        )} value: ${getSymbolicRepresentationForPath(valuePath)} sort-by: ${getSymbolicRepresentationForPath(
            keyPath
        )} )`;
    }

    public getContextPaths(): readonly Path[] {
        return [...super.getContextPaths(), this._offsetPath];
    }

    // This can happen when the position is a global path, for example.
    public processOtherDirt(proc: ProcessDirtProcessor): boolean {
        return proc.setDirty();
    }

    public recompute(
        proc: TableAggregateDataProvider<ExtractRowByPositionState>,
        _aggregatedTableDirtyState: DirtyState
    ): GroundValue {
        const offsetValue = getValueAt(proc.rootPathResolver, proc.getContextRow(), this._offsetPath);
        if (isLoadingValue(offsetValue)) return offsetValue;

        const offset = asMaybeNumber(offsetValue) ?? 0;

        let maybeState = proc.get();
        if (maybeState === undefined) {
            maybeState = { currentRowID: undefined, currentKey: undefined };
            proc.set(maybeState);
        }
        const state = maybeState;

        let table = proc.getAggregatedTable();
        if (isLoadingValue(table)) return table;
        if (table === undefined) {
            table = new Map();
        }

        // [row ID, key]
        const entries: [string, PrimitiveValue][] = [];

        let loadingValue: LoadingValue | undefined;
        tableForEach(table, false, r => {
            const key = this.getKeyForRow(r);
            if (isLoadingValue(key)) {
                loadingValue = key;
                return;
            }
            if (key === undefined) return;

            entries.push([r.$rowID, key]);
        });
        if (loadingValue !== undefined) return loadingValue;

        entries.sort(([, a], [, b]) => {
            if (a === b) return 0;
            return this.doesNewKeyOverride(a, b) ? -1 : 1;
        });

        const entry = entries[offset];

        state.currentRowID = entry?.[0];
        state.currentKey = entry?.[1];

        return this.getResultForState(state, table);
    }
}
export abstract class TableAggregateComputationBase<T = undefined> implements TableAggregateComputation<T> {
    public abstract readonly displayName: string;

    constructor(private readonly _keyPath: Path | undefined) {}

    public getContextPaths(): readonly Path[] {
        return [];
    }

    public getAggregatePaths(): readonly Path[] {
        if (this._keyPath === undefined) return [];
        return [this._keyPath];
    }

    protected getKeyForRow(ns: RootPathResolver, r: GroundValue): GroundValue {
        if (this._keyPath === undefined) return r;
        return getValueAt(ns, r, this._keyPath);
    }

    public processOtherDirt(proc: ProcessDirtProcessor): boolean {
        return proc.setDirty();
    }

    public abstract recompute(proc: TableAggregateDataProvider<T>): GroundValue;

    public get symbolicRepresentation(): string {
        return `(${this.displayName} ${getSymbolicRepresentationForPath(this._keyPath)})`;
    }
}

// This is an aggregate that computes the sum of contributions from each
// row.  It can be used for counting and summing.
abstract class CountComputation extends TableAggregateComputationBase {
    protected abstract getValue(v: LoadedGroundValue): number;
    protected abstract emptyValue: number | undefined;

    public recompute(proc: TableAggregateDataProvider<undefined>): GroundValue {
        let value = 0;
        let numRows = 0;
        const tableDefinedAndLoaded = proc.forEachInAggregated(row => {
            const key = this.getKeyForRow(proc.rootPathResolver, row);
            if (isLoadingValue(key)) return key;
            value += this.getValue(key);
            numRows++;
            return;
        });
        if (tableDefinedAndLoaded !== true) return tableDefinedAndLoaded;
        if (numRows === 0) return this.emptyValue;
        return value;
    }
}

export class CountNonEmptyComputation extends CountComputation {
    public readonly displayName = "count-non-empty";
    protected emptyValue = 0;

    protected getValue(v: LoadedGroundValue): number {
        return isNotEmpty(v) ? 1 : 0;
    }
}

export class CountTrueComputation extends CountComputation {
    public readonly displayName = "count-true";
    protected emptyValue = 0;

    protected getValue(v: LoadedGroundValue): number {
        if (!isPrimitive(v)) return 0;
        return areValuesEqual(v, true) === true ? 1 : 0;
    }
}

export class CountNotTrueComputation extends CountComputation {
    public readonly displayName = "count-not-true";
    protected emptyValue = 0;

    protected getValue(v: LoadedGroundValue): number {
        if (!isPrimitive(v)) return 0;
        return areValuesEqual(v, true) === true ? 0 : 1;
    }
}

export class SumComputation extends CountComputation {
    public readonly displayName = "sum";
    protected emptyValue = 0;

    protected getValue(v: LoadedGroundValue): number {
        return asMaybeNumber(v) ?? 0;
    }
}

// This could also be implemented as a `ReduceComputation`.
export class AverageComputation extends TableAggregateComputationBase {
    public readonly displayName = "average";

    public recompute(proc: TableAggregateDataProvider<undefined>): GroundValue {
        let s = 0;
        let n = 0;
        const isDefined = proc.forEachInAggregated(row => {
            const v = this.getKeyForRow(proc.rootPathResolver, row);
            if (isLoadingValue(v)) return v;
            const x = asMaybeNumber(v);
            if (x === undefined) {
                // This is just to make it compatible with the old computation
                // model.
                if (v !== undefined && v !== "") {
                    n++;
                }
                return;
            }
            s += x;
            n++;
            return;
        });
        if (isDefined !== true) return isDefined;
        if (n === 0) return undefined;
        return s / n;
    }
}

abstract class ReduceComputation<T> extends TableAggregateComputationBase {
    // Does one step in the reduction, i.e. reduces one row.  `current` is
    // always `undefined` for the first row.  For every following row it's
    // whatever the last `step` returned.
    //
    // TODO: We could be smarter here and allow individual aggregates to
    // decide whether they want to continue if a `LoadingValue` was
    // encountered.  For example, "all true" will evaluate to `false` is a
    // single `false` is found, not matter whether any other values are still
    // loading.
    protected abstract step(current: T | undefined, next: LoadedGroundValue): T | undefined;
    // Produce the result after all rows have been reduced.
    protected abstract finish(current: T | undefined): LoadedGroundValue;

    public recompute(proc: TableAggregateDataProvider<undefined>): GroundValue {
        let current: T | undefined;
        const isDefined = proc.forEachInAggregated(row => {
            const key = this.getKeyForRow(proc.rootPathResolver, row);
            if (isLoadingValue(key)) return key;
            current = this.step(current, key);
            return;
        });
        if (isDefined !== true) return isDefined;
        return this.finish(current);
    }
}

abstract class ExtremumComputation extends ReduceComputation<LoadedGroundValue> {
    protected abstract doesValueOverride(newValue: LoadedGroundValue, oldValue: LoadedGroundValue): boolean;

    protected step(current: LoadedGroundValue, next: LoadedGroundValue): LoadedGroundValue {
        if (this.doesValueOverride(next, current)) {
            return next;
        } else {
            return current;
        }
    }

    protected finish(current: LoadedGroundValue): LoadedGroundValue {
        return current;
    }
}

export class MinimumComputation extends ExtremumComputation {
    public readonly displayName = "minimum";

    protected doesValueOverride(newValue: LoadedGroundValue, oldValue: LoadedGroundValue): boolean {
        const newNumber = asMaybeNumber(newValue);
        if (newNumber === undefined) return false;

        const oldNumber = asMaybeNumber(oldValue);
        return oldNumber === undefined || newNumber < oldNumber;
    }
}

export class MaximumComputation extends ExtremumComputation {
    public readonly displayName = "maximum";

    protected doesValueOverride(newValue: LoadedGroundValue, oldValue: LoadedGroundValue): boolean {
        const newNumber = asMaybeNumber(newValue);
        if (newNumber === undefined) return false;

        const oldNumber = asMaybeNumber(oldValue);
        return oldNumber === undefined || newNumber > oldNumber;
    }
}

export class EarliestComputation extends ExtremumComputation {
    public readonly displayName = "earliest";

    protected doesValueOverride(newValue: LoadedGroundValue, oldValue: LoadedGroundValue): boolean {
        const newNumber = asMaybeDate(newValue);
        if (newNumber === undefined) return false;

        const oldNumber = asMaybeDate(oldValue);
        return oldNumber === undefined || newNumber < oldNumber;
    }
}

export class LatestComputation extends ExtremumComputation {
    public readonly displayName = "latest";

    protected doesValueOverride(newValue: LoadedGroundValue, oldValue: LoadedGroundValue): boolean {
        const newNumber = asMaybeDate(newValue);
        if (newNumber === undefined) return false;

        const oldNumber = asMaybeDate(oldValue);
        return oldNumber === undefined || newNumber > oldNumber;
    }
}

export class AllTrueComputation extends ReduceComputation<boolean> {
    public readonly displayName = "all-true";

    protected step(current: boolean | undefined, next: LoadedGroundValue): boolean {
        if (current === false) return false;
        return asBoolean(next);
    }

    protected finish(current: boolean | undefined): LoadedGroundValue {
        return current ?? true;
    }
}

export class SomeTrueComputation extends ReduceComputation<boolean> {
    public readonly displayName = "some-true";

    protected step(current: boolean | undefined, next: LoadedGroundValue): boolean {
        if (current === true) return true;
        return asBoolean(next);
    }

    protected finish(current: boolean | undefined): LoadedGroundValue {
        return current ?? false;
    }
}

export class RangeComputation extends ReduceComputation<[number, number]> {
    public readonly displayName = "range";

    protected step(current: [number, number] | undefined, next: LoadedGroundValue): [number, number] | undefined {
        const n = asMaybeNumber(next);
        if (n === undefined) return current;
        if (current === undefined) return [n, n];
        let [min, max] = current;
        if (n < min) {
            min = n;
        } else if (n > max) {
            max = n;
        }
        return [min, max];
    }

    protected finish(current: [number, number] | undefined): LoadedGroundValue {
        if (current === undefined) return undefined;
        return current[1] - current[0];
    }
}

type UniqueCountMap = DefaultMap<PrimitiveValue, number>;

function incrementUniqueCount(map: UniqueCountMap, key: PrimitiveValue): void {
    map.update(key, c => c + 1);
}

export class CountUniqueComputation extends TableAggregateComputationBase {
    public readonly displayName = "count-unique";

    public recompute(proc: TableAggregateDataProvider<undefined>): GroundValue {
        const countMap: UniqueCountMap = new DefaultMap(() => 0);
        const isDefined = proc.forEachInAggregated(row => {
            const key = this.getKeyForRow(proc.rootPathResolver, row);
            if (isLoadingValue(key)) return key;
            if (isNotEmpty(key)) {
                incrementUniqueCount(countMap, asPrimitive(key));
            }
            return;
        });
        if (isDefined !== true) return isDefined;
        return countMap.size;
    }
}

export class JoinStringsComputation extends TableAggregateComputationBase {
    public readonly displayName = "join-strings";
    private readonly _symbolicRepresentation: string;

    constructor(keyPath: Path | undefined, private readonly _separatorPath: Path) {
        super(keyPath);

        this._symbolicRepresentation = `(${this.displayName} ${getSymbolicRepresentationForPath(
            keyPath
        )} separator: ${getSymbolicRepresentationForPath(_separatorPath)})`;
    }

    public getContextPaths(): readonly Path[] {
        return [...super.getContextPaths(), this._separatorPath];
    }

    public recompute(proc: TableAggregateDataProvider<undefined>): GroundValue {
        const separatorValue = getValueAt(proc.rootPathResolver, proc.getContextRow(), this._separatorPath);
        if (isLoadingValue(separatorValue)) return separatorValue;

        const separator = asMaybeString(separatorValue ?? "");
        if (separator === undefined) return undefined;

        const parts: string[] = [];
        const isDefined = proc.forEachInAggregated(row => {
            const keyValue = this.getKeyForRow(proc.rootPathResolver, row);
            if (isLoadingValue(keyValue)) return keyValue;
            const key = asMaybeString(keyValue);
            if (key === undefined) return;
            parts.push(key);
            return;
        });
        if (isDefined !== true) return isDefined;

        return parts.join(separator);
    }

    public get symbolicRepresentation(): string {
        return this._symbolicRepresentation;
    }
}

// A lookup of primitive values from a multi-relation.  This produces an array
// of primitive values.
export class MultiPrimitiveLookupComputation extends TableAggregateComputationBase {
    public readonly displayName = "multi-primitive-lookup";

    public recompute(proc: TableAggregateDataProvider<undefined>): GroundValue {
        const table = proc.getAggregatedTable();
        if (table === undefined || isLoadingValue(table)) return table;

        let loadingValue: LoadingValue | undefined;
        const values: PrimitiveValue[] = [];
        tableForEach(table, false, r => {
            if (loadingValue !== undefined) return;
            const k = this.getKeyForRow(proc.rootPathResolver, r);
            if (isLoadingValue(k)) {
                loadingValue = k;
                return;
            }
            if (isPrimitive(k) && isNotEmpty(k)) {
                values.push(asPrimitive(k));
            }
        });
        if (loadingValue !== undefined) return loadingValue;
        return values;
    }
}

// A lookup of single- or multi-relations from a multi-relation.  This
// produces a multi-relation, i.e. a table.
export class MultiRelationLookupComputation extends TableAggregateComputationBase {
    public readonly displayName = "multi-relation-lookup";

    public recompute(proc: TableAggregateDataProvider<undefined>): GroundValue {
        const table = proc.getAggregatedTable();
        if (table === undefined || isLoadingValue(table)) return table;

        let loadingValue: LoadingValue | undefined;
        const result: Table = new Map();
        tableForEach(table, false, r => {
            if (loadingValue !== undefined) return;
            const k = this.getKeyForRow(proc.rootPathResolver, r);
            if (k === undefined) return;
            if (isLoadingValue(k)) {
                loadingValue = k;
                return;
            }
            if (isTable(k)) {
                for (const v of k.values()) {
                    result.set(v.$rowID, v);
                }
            } else if (isRow(k)) {
                result.set(k.$rowID, k);
            } else {
                return panic("Multi-relation lookup target must be a row or a table");
            }
            return;
        });
        if (loadingValue !== undefined) return loadingValue;
        return result;
    }
}

export class ExtractNthItemComputation extends TableAggregateComputationBase {
    public readonly displayName = "extract-nth-item";
    private readonly _symbolicRepresentation: string;

    constructor(
        keyPath: Path | undefined,
        private readonly _position: FirstOrLast,
        private readonly _offsetPath: Path
    ) {
        super(keyPath);

        this._symbolicRepresentation = `(${this.displayName} ${getSymbolicRepresentationForPath(
            keyPath
        )} offset: ${getSymbolicRepresentationForPath(_offsetPath)})`;
    }

    public getContextPaths(): readonly Path[] {
        return [...super.getContextPaths(), this._offsetPath];
    }

    public recompute(proc: TableAggregateDataProvider<undefined>): GroundValue {
        const offsetValue = getValueAt(proc.rootPathResolver, proc.getContextRow(), this._offsetPath);
        if (isLoadingValue(offsetValue)) return offsetValue;

        let offset = asMaybeNumber(offsetValue) ?? 0;
        if (offset === undefined) return undefined;

        const items = proc.getAggregatedAsArray();
        if (items === undefined || isLoadingValue(items)) return items;

        if (this._position === FirstOrLast.Last) {
            offset = items.length - 1 - offset;
        }

        return items[offset];
    }

    public get symbolicRepresentation(): string {
        return this._symbolicRepresentation;
    }
}
